import { useContext } from 'react';
import { ThemeCustomContext } from '../contexts/ThemeCustomContext';

/**
 * Hook para acessar o contexto de temas customizados
 * Fornece acesso fácil às configurações do tema ativo
 * 
 * @returns {Object} Contexto do tema com as seguintes propriedades:
 * - currentTheme: Configuração completa do tema atual
 * - themeId: ID do tema ativo
 * - loading: Estado de carregamento do tema
 * - availableThemes: Lista de temas disponíveis
 * - changeTheme(themeId): Função para trocar de tema
 * - getThemeValue(path): Obtém um valor do tema pelo caminho (ex: 'colors.primary')
 * - getIcon(iconKey): Obtém configuração de ícone
 * - getLabel(labelKey): Obtém um label/texto
 * - getMessage(messageKey): Obtém uma mensagem
 * - getColor(colorKey): Obtém uma cor
 * 
 * @example
 * // Uso básico
 * const { getLabel, getColor, getIcon } = useThemeCustom();
 * 
 * // Obter label
 * const title = getLabel('appTitle'); // "Agendário" ou "BarberShop"
 * 
 * // Obter cor
 * const primaryColor = getColor('primary'); // "#1976d2" ou "#8B4513"
 * 
 * // Obter ícone
 * const serviceIcon = getIcon('services'); // { name: "ContentCut" } ou { name: "ContentCut", color: "#8B4513" }
 * 
 * // Trocar tema
 * changeTheme('barbershop');
 */
export function useThemeCustom() {
  const context = useContext(ThemeCustomContext);

  if (!context) {
    console.error('[useThemeCustom] ❌ ERRO CRÍTICO: ThemeCustomContext é NULL/UNDEFINED');
    console.error('[useThemeCustom] Hook não está dentro de ThemeCustomProvider');
    throw new Error('useThemeCustom deve ser usado dentro de um ThemeCustomProvider');
  }

  console.debug('[useThemeCustom] ✅ Context obtido com sucesso:', {
    hasGetLabel: typeof context.getLabel === 'function',
    hasGetIcon: typeof context.getIcon === 'function',
    themeId: context.themeId,
    loading: context.loading,
  });

  return context;
}
